import { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FileCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MainLayout } from '@/components/layout/MainLayout';
import {
  DeclarationsTab,
  DraftCopiesTab,
  FinalCopiesTab,
  TaxSummariesTab,
  TAX_FILINGS_TAB,
  TAX_FILINGS_TAB_VALUES,
  type TaxFilingsTab,
} from '@/features/tax-filings';

function parseTab(raw: string | undefined): TaxFilingsTab {
  if (raw && (TAX_FILINGS_TAB_VALUES as string[]).includes(raw)) {
    return raw as TaxFilingsTab;
  }
  return TAX_FILINGS_TAB.TAX_SUMMARY;
}

export const TaxFilingsPage = () => {
  const { tab: tabParam } = useParams<{ tab: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeTab = useMemo(() => parseTab(tabParam), [tabParam]);

  useEffect(() => {
    const legacyTab = searchParams.get('tab');
    if (legacyTab && (TAX_FILINGS_TAB_VALUES as string[]).includes(legacyTab)) {
      navigate(`/tax-filings/${legacyTab}`, { replace: true });
      return;
    }
    if (tabParam && !(TAX_FILINGS_TAB_VALUES as string[]).includes(tabParam)) {
      navigate('/tax-filings/tax-summary', { replace: true });
    }
  }, [tabParam, searchParams, navigate]);

  const handleTabChange = (value: string) => {
    const next = parseTab(value);
    navigate(`/tax-filings/${next}`, { replace: true });
  };

  return (
    <MainLayout>
      <div className="container mx-auto py-6 px-4 sm:py-8 max-w-5xl">
        <div className="space-y-6 sm:space-y-8">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
              Tax Filings
            </h1>
            <p className="text-muted-foreground">
              View tax summaries, review draft copies, sign declarations, and download final copies
            </p>
          </div>

          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileCheck className="h-5 w-5 text-primary" />
                Filing Documents
              </CardTitle>
              <CardDescription className="mt-1.5">
                Documents are scoped to the selected tax year
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto gap-1 p-1">
                  <TabsTrigger
                    value={TAX_FILINGS_TAB.TAX_SUMMARY}
                    className="data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:font-semibold"
                  >
                    Tax Summary
                  </TabsTrigger>
                  <TabsTrigger
                    value={TAX_FILINGS_TAB.DRAFT_COPIES}
                    className="data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:font-semibold"
                  >
                    Draft Copies
                  </TabsTrigger>
                  <TabsTrigger
                    value={TAX_FILINGS_TAB.DECLARATION}
                    className="data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:font-semibold"
                  >
                    Declaration
                  </TabsTrigger>
                  <TabsTrigger
                    value={TAX_FILINGS_TAB.FINAL_COPIES}
                    className="data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:font-semibold"
                  >
                    Final Copies
                  </TabsTrigger>
                </TabsList>

                <TabsContent value={TAX_FILINGS_TAB.TAX_SUMMARY} className="mt-4">
                  <TaxSummariesTab />
                </TabsContent>
                <TabsContent value={TAX_FILINGS_TAB.DRAFT_COPIES} className="mt-4">
                  <DraftCopiesTab />
                </TabsContent>
                <TabsContent value={TAX_FILINGS_TAB.DECLARATION} className="mt-4">
                  <DeclarationsTab />
                </TabsContent>
                <TabsContent value={TAX_FILINGS_TAB.FINAL_COPIES} className="mt-4">
                  <FinalCopiesTab />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};
